import { useState, useMemo } from 'react';
import { Search, Plus, User, DollarSign, Trash2, Pencil } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useMaestros,
  useMaestro,
  useCreateMaestro,
  useUpdateMaestro,
  useAddBaseRate,
  useUpdateBaseRate,
  useDeleteMaestro,
  type MaestroListDTO,
  type MaestroDetailDTO,
  type MaestroRateDTO,
} from '../../hooks/useMaestros';
import { useRecipes, useRecipe } from '../../hooks/useRecipes';
import { useAuthStore } from '../../stores/auth-store';
import {
  createMaestroSchema,
  updateMaestroSchema,
  type CreateMaestroInput,
  type UpdateMaestroInput,
} from '@shared/validation/maestro-schemas';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { SkeletonTable } from '../../components/ui/Skeleton';

const ESPECIALIDAD_LABELS: Record<string, string> = {
  gypsum: 'Gypsum',
  pintura: 'Pintura',
  impermeabilizacion: 'Impermeabilización',
  empaste: 'Empaste',
};

const ESPECIALIDAD_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'gypsum', label: 'Gypsum' },
  { value: 'pintura', label: 'Pintura' },
  { value: 'impermeabilizacion', label: 'Impermeabilización' },
  { value: 'empaste', label: 'Empaste' },
];

export function MaestrosPage() {
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout>>();
  const [especialidadFilter, setEspecialidadFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    setDebounceTimer(timer);
  };

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      especialidad: especialidadFilter || undefined,
      page,
      limit: 20,
    }),
    [debouncedSearch, especialidadFilter, page],
  );

  const { data, isLoading, isError, refetch } = useMaestros(filters);

  if (isLoading) {
    return (
      <div className="space-y-6 fade-up-1">
        <h1 className="text-2xl font-bold text-brand-dark">Maestros</h1>
        <Card><div className="p-6"><SkeletonTable rows={5} cols={4} /></div></Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 fade-up-1">
        <h1 className="text-2xl font-bold text-brand-dark">Maestros</h1>
        <Card>
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">Error al cargar maestros</p>
            <Button variant="secondary" className="mt-4" onClick={() => refetch()}>Reintentar</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-up-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-dark">Maestros</h1>
        {canEdit && (
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => setShowCreateModal(true)}
          >
            Nuevo Maestro
          </Button>
        )}
      </div>

      {/* Search + Filter */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_200px]">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            placeholder="Buscar por nombre o cedula..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <select
          className="rounded-lg border border-gray-200 bg-white/80 px-3 py-2 text-sm text-gray-700 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          value={especialidadFilter}
          onChange={(e) => { setEspecialidadFilter(e.target.value); setPage(1); }}
        >
          {ESPECIALIDAD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Master-Detail layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        {/* Left: List */}
        <Card>
          {data && data.data.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <User size={48} className="mx-auto text-gray-300" />
              <h2 className="mt-4 text-lg font-semibold text-gray-600">No hay maestros registrados</h2>
              <p className="mt-1 text-sm text-gray-400">Agrega el primer maestro para gestionar sus tarifas.</p>
              {canEdit && (
                <Button variant="primary" className="mt-4" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
                  Nuevo Maestro
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {data?.data.map((maestro) => (
                <MaestroListItem
                  key={maestro.id}
                  maestro={maestro}
                  isSelected={selectedId === maestro.id}
                  onClick={() => setSelectedId(maestro.id)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 border-t border-gray-100 px-4 py-3">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
              <span className="text-sm text-gray-500">Pag. {page}/{data.pagination.totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          )}
        </Card>

        {/* Right: Detail */}
        <div>
          {selectedId ? (
            <MaestroDetailPanel maestroId={selectedId} canEdit={canEdit} />
          ) : (
            <Card>
              <div className="px-6 py-16 text-center">
                <User size={48} className="mx-auto text-gray-300" />
                <p className="mt-4 text-sm text-gray-400">Selecciona un maestro para ver su perfil</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateMaestroModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(id) => setSelectedId(id)}
        />
      )}
    </div>
  );
}

// ── List Item ─────────────────────────────────────────────────────────

function MaestroListItem({
  maestro,
  isSelected,
  onClick,
}: {
  maestro: MaestroListDTO;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`w-full px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
        isSelected ? 'bg-brand-blue/5 border-l-2 border-brand-blue' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-gray-800">{maestro.nombre}</p>
          {maestro.cedula && <p className="text-xs text-gray-400">{maestro.cedula}</p>}
        </div>
        <div className="flex items-center gap-1.5">
          {maestro.is_planta && (
            <Badge color="purple">De planta</Badge>
          )}
          {!maestro.is_active && (
            <Badge color="red">Inactivo</Badge>
          )}
        </div>
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {maestro.especialidades.map((esp) => (
          <span key={esp} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            {ESPECIALIDAD_LABELS[esp] ?? esp}
          </span>
        ))}
        {maestro.proyectos_activos > 0 && (
          <span className="text-xs text-gray-400">{maestro.proyectos_activos} proyecto(s)</span>
        )}
      </div>
    </button>
  );
}

// ── Detail Panel ──────────────────────────────────────────────────────

function MaestroDetailPanel({ maestroId, canEdit }: { maestroId: string; canEdit: boolean }) {
  const { data: detail, isLoading } = useMaestro(maestroId);
  const [showAddRate, setShowAddRate] = useState(false);
  const [editingRate, setEditingRate] = useState<MaestroRateDTO | null>(null);
  const [showEditMaestro, setShowEditMaestro] = useState(false);

  if (isLoading) {
    return <Card><div className="p-6 text-center text-sm text-gray-400">Cargando perfil...</div></Card>;
  }

  if (!detail) {
    return <Card><div className="p-6 text-center text-sm text-gray-400">No se pudo cargar el perfil</div></Card>;
  }

  return (
    <div className="space-y-4">
      {/* Personal info */}
      <Card>
        <div className="p-4">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold text-gray-800">{detail.nombre}</h3>
            {canEdit && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowEditMaestro(true)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-brand-blue"
                  title="Editar maestro"
                >
                  <Pencil size={15} />
                </button>
                <DeleteMaestroButton maestroId={detail.id} />
              </div>
            )}
          </div>
          <div className="mt-2 space-y-1 text-sm text-gray-600">
            {detail.cedula && <p>Cedula: {detail.cedula}</p>}
            {detail.telefono && <p>Telefono: {detail.telefono}</p>}
            <div className="flex flex-wrap gap-1 pt-1">
              {detail.especialidades.map((esp) => (
                <span key={esp} className="rounded-full bg-brand-blue/10 px-2 py-0.5 text-xs font-medium text-brand-blue">
                  {ESPECIALIDAD_LABELS[esp] ?? esp}
                </span>
              ))}
              {detail.is_planta && (
                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                  Personal de planta
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Base rates */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 font-semibold text-gray-700">
              <DollarSign size={16} />
              Precios acordados base
            </h4>
            {canEdit && (
              <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={() => setShowAddRate(true)}>
                Agregar
              </Button>
            )}
          </div>
          {detail.base_rates.length === 0 ? (
            <p className="mt-3 text-sm text-gray-400">Sin tarifas base configuradas</p>
          ) : (
            <div className="mt-3 space-y-2">
              {detail.base_rates.map((rate) => (
                <div key={rate.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-700">{rate.recipe_nombre}</p>
                    <p className="text-xs text-gray-400">{rate.recipe_codigo}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">
                      ${rate.precio_unitario.toFixed(2)}/{rate.unidad}
                    </span>
                    {canEdit && (
                      <button
                        className="text-xs text-brand-blue hover:underline"
                        onClick={() => setEditingRate(rate)}
                      >
                        Editar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Active projects */}
      <Card>
        <div className="p-4">
          <h4 className="font-semibold text-gray-700">Proyectos activos</h4>
          {detail.active_projects.length === 0 ? (
            <p className="mt-2 text-sm text-gray-400">Sin proyectos activos</p>
          ) : (
            <div className="mt-2 space-y-1">
              {detail.active_projects.map((proj) => (
                <div key={proj.project_id} className="flex items-center justify-between rounded bg-gray-50 px-3 py-2 text-sm">
                  <span className="text-gray-700">{proj.project_nombre}</span>
                  <span className="text-xs text-gray-400">{proj.project_codigo}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Add Rate Modal */}
      {showAddRate && (
        <AddRateModal
          maestroId={maestroId}
          existingRecipeIds={detail.base_rates.map((r) => r.recipe_id)}
          onClose={() => setShowAddRate(false)}
        />
      )}

      {/* Edit Rate Modal */}
      {editingRate && (
        <EditRateModal
          maestroId={maestroId}
          rate={editingRate}
          onClose={() => setEditingRate(null)}
        />
      )}

      {/* Edit Maestro Modal */}
      {showEditMaestro && (
        <EditMaestroModal
          maestro={detail}
          onClose={() => setShowEditMaestro(false)}
        />
      )}
    </div>
  );
}

// ── Create Maestro Modal ──────────────────────────────────────────────

function CreateMaestroModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const createMutation = useCreateMaestro();
  const [selectedEspecialidades, setSelectedEspecialidades] = useState<string[]>([]);
  const [isPlanta, setIsPlanta] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CreateMaestroInput>({
    resolver: zodResolver(createMaestroSchema),
    defaultValues: { especialidades: [] },
  });

  const toggleEspecialidad = (esp: string) => {
    const next = selectedEspecialidades.includes(esp)
      ? selectedEspecialidades.filter((e) => e !== esp)
      : [...selectedEspecialidades, esp];
    setSelectedEspecialidades(next);
    setValue('especialidades', next as CreateMaestroInput['especialidades'], { shouldValidate: true });
  };

  const onSubmit = handleSubmit(async (data) => {
    const result = await createMutation.mutateAsync({ ...data, isPlanta });
    onClose();
    onCreated(result.id);
  });

  return (
    <Modal isOpen title="Nuevo Maestro" onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Nombre completo" {...register('nombre')} error={errors.nombre?.message} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Cedula" {...register('cedula')} error={errors.cedula?.message} placeholder="1234567890" />
          <Input label="Telefono" {...register('telefono')} error={errors.telefono?.message} placeholder="0991234567" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Especialidades</label>
          <div className="flex flex-wrap gap-2">
            {(['gypsum', 'pintura', 'impermeabilizacion', 'empaste'] as const).map((esp) => (
              <button
                key={esp}
                type="button"
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                  selectedEspecialidades.includes(esp)
                    ? 'bg-brand-blue text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => toggleEspecialidad(esp)}
              >
                {ESPECIALIDAD_LABELS[esp]}
              </button>
            ))}
          </div>
          {errors.especialidades?.message && (
            <p className="mt-1 text-xs text-red-500">{errors.especialidades.message}</p>
          )}
        </div>
        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-brand-blue"
            checked={isPlanta}
            onChange={(e) => setIsPlanta(e.target.checked)}
          />
          <div>
            <p className="text-sm font-medium text-gray-700">Personal de planta</p>
            <p className="text-xs text-gray-400">Sus planillas no aparecen en los reportes de pagos</p>
          </div>
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button variant="primary" type="submit" isLoading={createMutation.isPending}>Crear Maestro</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit Maestro Modal ────────────────────────────────────────────────

function EditMaestroModal({
  maestro,
  onClose,
}: {
  maestro: MaestroDetailDTO;
  onClose: () => void;
}) {
  const updateMutation = useUpdateMaestro();
  const [selectedEspecialidades, setSelectedEspecialidades] = useState<string[]>(
    maestro.especialidades ?? [],
  );
  const [isPlanta, setIsPlanta] = useState(maestro.is_planta);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<UpdateMaestroInput>({
    resolver: zodResolver(updateMaestroSchema),
    defaultValues: {
      nombre: maestro.nombre,
      cedula: maestro.cedula ?? undefined,
      telefono: maestro.telefono ?? undefined,
      especialidades: maestro.especialidades as UpdateMaestroInput['especialidades'],
    },
  });

  const toggleEspecialidad = (esp: string) => {
    const next = selectedEspecialidades.includes(esp)
      ? selectedEspecialidades.filter((e) => e !== esp)
      : [...selectedEspecialidades, esp];
    setSelectedEspecialidades(next);
    setValue('especialidades', next as UpdateMaestroInput['especialidades'], { shouldValidate: true });
  };

  const onSubmit = handleSubmit(async (data) => {
    await updateMutation.mutateAsync({ id: maestro.id, data: { ...data, isPlanta } });
    onClose();
  });

  return (
    <Modal isOpen title="Editar Maestro" onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Nombre completo" {...register('nombre')} error={errors.nombre?.message} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Cedula" {...register('cedula')} error={errors.cedula?.message} placeholder="1234567890" />
          <Input label="Telefono" {...register('telefono')} error={errors.telefono?.message} placeholder="0991234567" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Especialidades</label>
          <div className="flex flex-wrap gap-2">
            {(['gypsum', 'pintura', 'impermeabilizacion', 'empaste'] as const).map((esp) => (
              <button
                key={esp}
                type="button"
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                  selectedEspecialidades.includes(esp)
                    ? 'bg-brand-blue text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => toggleEspecialidad(esp)}
              >
                {ESPECIALIDAD_LABELS[esp]}
              </button>
            ))}
          </div>
          {errors.especialidades?.message && (
            <p className="mt-1 text-xs text-red-500">{errors.especialidades.message}</p>
          )}
        </div>
        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-brand-blue"
            checked={isPlanta}
            onChange={(e) => setIsPlanta(e.target.checked)}
          />
          <div>
            <p className="text-sm font-medium text-gray-700">Personal de planta</p>
            <p className="text-xs text-gray-400">Sus planillas no aparecen en los reportes de pagos</p>
          </div>
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button variant="primary" type="submit" isLoading={updateMutation.isPending}>Guardar cambios</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Add Rate Modal ────────────────────────────────────────────────────

function AddRateModal({
  maestroId,
  existingRecipeIds,
  onClose,
}: {
  maestroId: string;
  existingRecipeIds: string[];
  onClose: () => void;
}) {
  const addRate = useAddBaseRate();
  const { data: recipesData } = useRecipes({ limit: 100 });
  const [recipeId, setRecipeId] = useState('');
  const [precio, setPrecio] = useState('');
  const [unidad, setUnidad] = useState<'m2' | 'ml' | 'unid' | 'kg' | 'glb'>('m2');
  const { data: recipeDetail } = useRecipe(recipeId);

  const availableRecipes = useMemo(
    () =>
      (recipesData?.data ?? [])
        .filter((r) => !existingRecipeIds.includes(r.id))
        .map((r) => ({ value: r.id, label: `${r.codigo} — ${r.nombre}` })),
    [recipesData, existingRecipeIds],
  );

  // MDO items from selected recipe
  const recipeMdoItems = useMemo(
    () => recipeDetail?.materials.filter((m) => m.is_mdo) ?? [],
    [recipeDetail],
  );

  // Editable MDO prices (initialized from recipe defaults)
  const [mdoPrices, setMdoPrices] = useState<Map<string, number>>(new Map());

  // Reset MDO prices when recipe changes
  const handleRecipeChange = (newRecipeId: string) => {
    setRecipeId(newRecipeId);
    setPrecio('');
    setMdoPrices(new Map());
  };

  const getEffectivePrice = (materialId: string, recipePrice: number) =>
    mdoPrices.has(materialId) ? mdoPrices.get(materialId)! : recipePrice;

  const mdoTotal = useMemo(() => {
    return recipeMdoItems.reduce(
      (sum, m) => sum + getEffectivePrice(m.material_id, m.material_precio_unitario * m.cantidad_por_unidad),
      0,
    );
  }, [recipeMdoItems, mdoPrices]);

  const displayPrecio = precio || (mdoTotal > 0 ? mdoTotal.toFixed(2) : '');

  const handleMdoPriceChange = (materialId: string, value: number) => {
    const newMap = new Map(mdoPrices);
    newMap.set(materialId, value);
    setMdoPrices(newMap);
  };

  const handleSubmit = async () => {
    if (!recipeId || !displayPrecio) return;
    const mdoItems = recipeMdoItems.map((m) => ({
      materialId: m.material_id,
      precioUnitario: getEffectivePrice(m.material_id, m.material_precio_unitario * m.cantidad_por_unidad),
    }));
    await addRate.mutateAsync({
      maestroId,
      data: {
        recipeId,
        precioUnitario: Number(mdoTotal > 0 ? mdoTotal.toFixed(2) : displayPrecio),
        unidad,
        mdoItems: mdoItems.length > 0 ? mdoItems : undefined,
      },
    });
    onClose();
  };

  return (
    <Modal isOpen title="Agregar tarifa base" onClose={onClose} size="md">
      <div className="space-y-4">
        <Select
          label="APU"
          value={recipeId}
          onChange={(e) => handleRecipeChange(e.target.value)}
          options={availableRecipes}
          placeholder="Seleccionar APU..."
        />

        {/* MDO items — editable */}
        {recipeId && recipeMdoItems.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500">Precios de Mano de Obra:</p>
            {recipeMdoItems.map((m) => {
              const recipeDefault = m.material_precio_unitario * m.cantidad_por_unidad;
              const currentPrice = getEffectivePrice(m.material_id, recipeDefault);
              return (
                <div key={m.material_id} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-2">
                  <span className="flex-1 text-sm text-gray-700">{m.material_nombre}</span>
                  <div className="w-24">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={currentPrice}
                      onChange={(e) => handleMdoPriceChange(m.material_id, Number(e.target.value) || 0)}
                      className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-[#3c98ff] focus:outline-none"
                    />
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between rounded-lg bg-blue-50 p-2 mt-1">
              <span className="text-sm font-semibold text-blue-800">Total MDO</span>
              <span className="text-sm font-semibold text-blue-800">${mdoTotal.toFixed(2)}</span>
            </div>
          </div>
        )}

        {recipeId && recipeMdoItems.length === 0 && recipeDetail && (
          <p className="text-xs text-gray-400">Este APU no tiene ítems de Mano de Obra</p>
        )}

        <div className={`grid gap-4 ${recipeMdoItems.length > 0 ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {recipeMdoItems.length === 0 && (
            <Input
              label="Precio unitario ($)"
              type="number"
              step="0.01"
              value={displayPrecio}
              onChange={(e) => setPrecio(e.target.value)}
              placeholder="0.00"
            />
          )}
          <Select
            label="Unidad"
            value={unidad}
            onChange={(e) => setUnidad(e.target.value as typeof unidad)}
            options={[
              { value: 'm2', label: 'm2' },
              { value: 'ml', label: 'ml' },
              { value: 'unid', label: 'unid' },
              { value: 'kg', label: 'kg' },
              { value: 'glb', label: 'glb' },
            ]}
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={addRate.isPending}
            disabled={!recipeId || !displayPrecio}
          >
            Guardar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Edit Rate Modal ───────────────────────────────────────────────────

function EditRateModal({
  maestroId,
  rate,
  onClose,
}: {
  maestroId: string;
  rate: MaestroRateDTO;
  onClose: () => void;
}) {
  const updateRate = useUpdateBaseRate();
  const { data: recipeDetail } = useRecipe(rate.recipe_id);

  // Get MDO items from recipe, with maestro-specific prices if they exist
  const recipeMdoItems = useMemo(
    () => recipeDetail?.materials.filter((m) => m.is_mdo) ?? [],
    [recipeDetail],
  );

  // Initialize editable prices: use maestro's saved prices if available, otherwise recipe defaults
  const [mdoPrices, setMdoPrices] = useState<Map<string, number>>(() => {
    const map = new Map<string, number>();
    for (const item of rate.mdo_items ?? []) {
      map.set(item.material_id, item.precio_unitario);
    }
    return map;
  });

  // Effective prices: maestro custom → recipe default
  const getEffectivePrice = (materialId: string, recipePrice: number) =>
    mdoPrices.has(materialId) ? mdoPrices.get(materialId)! : recipePrice;

  const total = useMemo(() => {
    if (recipeMdoItems.length === 0) return Number(rate.precio_unitario);
    return recipeMdoItems.reduce(
      (sum, m) => sum + getEffectivePrice(m.material_id, m.material_precio_unitario * m.cantidad_por_unidad),
      0,
    );
  }, [recipeMdoItems, mdoPrices, rate.precio_unitario]);

  const handlePriceChange = (materialId: string, value: number) => {
    const newMap = new Map(mdoPrices);
    newMap.set(materialId, value);
    setMdoPrices(newMap);
  };

  const handleSubmit = async () => {
    const mdoItems = recipeMdoItems.map((m) => ({
      materialId: m.material_id,
      precioUnitario: getEffectivePrice(m.material_id, m.material_precio_unitario * m.cantidad_por_unidad),
    }));

    await updateRate.mutateAsync({
      maestroId,
      rateId: rate.id,
      data: {
        precioUnitario: Number(total.toFixed(2)),
        mdoItems: mdoItems.length > 0 ? mdoItems : undefined,
      },
    });
    onClose();
  };

  return (
    <Modal isOpen title={`Editar tarifa: ${rate.recipe_nombre}`} onClose={onClose} size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          {rate.recipe_codigo} — {rate.unidad}
        </p>

        {recipeMdoItems.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500">Precios de Mano de Obra:</p>
            {recipeMdoItems.map((m) => {
              const recipeDefault = m.material_precio_unitario * m.cantidad_por_unidad;
              const currentPrice = getEffectivePrice(m.material_id, recipeDefault);
              return (
                <div key={m.material_id} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-2">
                  <span className="flex-1 text-sm text-gray-700">{m.material_nombre}</span>
                  <div className="w-24">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={currentPrice}
                      onChange={(e) => handlePriceChange(m.material_id, Number(e.target.value) || 0)}
                      className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-[#3c98ff] focus:outline-none"
                    />
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between rounded-lg bg-blue-50 p-2 mt-1">
              <span className="text-sm font-semibold text-blue-800">Total MDO</span>
              <span className="text-sm font-semibold text-blue-800">${total.toFixed(2)}</span>
            </div>
          </div>
        ) : (
          <Input
            label="Precio unitario ($)"
            type="number"
            step="0.01"
            value={String(total)}
            onChange={() => {}}
          />
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={updateRate.isPending}
          >
            Guardar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DeleteMaestroButton({ maestroId }: { maestroId: string }) {
  const deleteMutation = useDeleteMaestro();
  return (
    <button
      className="text-gray-400 hover:text-red-500"
      title="Desactivar maestro"
      onClick={async () => {
        if (window.confirm('¿Desactivar este maestro? No aparecerá en futuras búsquedas.')) {
          await deleteMutation.mutateAsync(maestroId);
        }
      }}
    >
      <Trash2 size={16} />
    </button>
  );
}
